import {
  AnalyticsPayload,
  renderTemplate,
  renderTemplatesWithAssets,
  SB,
} from '@play-co/replicant';
import { replicantAssets } from '../creatives/assets';
import { AnyPayload } from '../State';
import { chatbotFlexTemplates } from './flex';
import { chatbotSimpleTemplates } from './simple';
import { buildSender } from './sender';
import { getPetAssetKey, speciesChatbot } from './flex/species';
import { AlternativeOpenWebBuckets } from '../utils/payload';
import { getAlternativeWebIABBucket } from './chatbot.util';
import { quickReplyChatbotMessage } from './simple/reply';

export const chatbotAssets = {
  ...replicantAssets,
};

export type ChatbotAssetKey = keyof typeof chatbotAssets;

// We should use it for all future chatbot messages ChatbotMessageSuccess/ChatbotMessageFailure
//
// Declared separately from the `& AnyPayload` index signature below so that
// `Omit`/`Pick` over these keys behave. Applied to the intersection, `keyof`
// widens to `string | number` and every declared field is silently erased.
type ChatbotMessagePayloadFields = {
  $channel: string;
  feature: string;
  $subFeature: string;
  $creativeAssetID?: string;
  $creativeCTA?: string;
  $creativeTextID?: string;
  contextID?: string;
  templateID?: string;
  carouselMessages?: number;
  template?: string;
  alternativeWebIABBucket: AlternativeOpenWebBuckets;
  originFeature?: string;
  originSubFeature?: string;
  friendRequestSenderID?: string;
  friendRequestReceiverID?: string;
  productID?: string;
  purchaseID?: string;
};

export type ChatbotMessagePayload = ChatbotMessagePayloadFields & AnyPayload;

/**
 * What a caller supplies to `generateReplicantChatbotPayload`, as opposed to
 * the fully materialized payload it returns: `$channel` is defaulted and
 * `alternativeWebIABBucket` is computed, so neither is the caller's to pass.
 */
export type ChatbotMessagePayloadInput = Omit<
  ChatbotMessagePayloadFields,
  '$channel' | 'alternativeWebIABBucket'
> & { $channel?: string };

const chatbotMessageTemplates = renderTemplatesWithAssets(chatbotAssets, {
  petChat: renderTemplate({
    args: SB.object({
      message: SB.string(),
      species: SB.tuple(speciesChatbot),
      petName: SB.string(),
    }),
    renderers: {
      line: ({ args, api }) => {
        const { species, message, petName } = args;
        return {
          type: 'text',
          text: message,
          sender: buildSender({
            name: petName,
            iconUrl: api.getAssetPath(getPetAssetKey(species)),
          }),
        };
      },
      ios: ({ args, payload }) =>
        iosGenericTemplate({
          alert: args.message,
          payload,
        }),
      android: ({ args, payload }) =>
        androidGenericTemplate({
          title: args.message,
          payload,
        }),
    },
  }),

  simplePushNotification: renderTemplate({
    args: SB.object({
      title: SB.string(),
      body: SB.string(),
    }),
    renderers: {
      ios: ({ args, payload }) =>
        iosGenericTemplate({
          alert: {
            title: args.title,
            body: args.body,
          },
          payload,
        }),
      android: ({ args, payload }) =>
        androidGenericTemplate({
          title: args.title,
          body: args.body,
          payload,
        }),
    },
  }),

  // Quick reply chatbot message
  ...quickReplyChatbotMessage,

  // Add simple message templates
  ...chatbotSimpleTemplates,

  // Add flex messages templates
  ...chatbotFlexTemplates,
});

/**
 * @deprecated use generateChatbotPayload from src/lib/chatbot.ts instead
 * or generateReplicantChatbotPayload for replicant messages
 */
export function generateChatbotPayloadOld(
  feature: string,
  subFeature?: string,
) {
  return {
    $channel: 'CHATBOT',
    feature,
    $subFeature: subFeature || null,
  };
}

/**
 * Use this function for generation chatbot payload pn replicant side
 */
export function generateReplicantChatbotPayload(
  payload: ChatbotMessagePayloadInput,
): ChatbotMessagePayload {
  // It will override same properties from GCInstant.getPlatformPayloadData(),
  const messagePayload: ChatbotMessagePayload = {
    $channel: payload.$channel ?? 'CHATBOT',
    feature: payload.feature,
    $subFeature: payload.$subFeature,
    $creativeAssetID: payload.$creativeAssetID,
    $creativeCTA: payload.$creativeCTA,
    $creativeTextID: payload.$creativeTextID,
    contextID: payload.contextID,
    templateID: payload.templateID,
    alternativeWebIABBucket: getAlternativeWebIABBucket(),
    originFeature: payload.originFeature,
    originSubFeature: payload.originSubFeature,
    friendRequestSenderID: payload.friendRequestSenderID,
    friendRequestReceiverID: payload.friendRequestReceiverID,
    productID: payload.productID,
    purchaseID: payload.purchaseID,
  };

  return {
    ...messagePayload,
  };
}

export function iosGenericTemplate(opts: {
  alert:
    | string
    | {
        title: string;
        subtitle?: string;
        body: string;
        'launch-image'?: string;
      };
  payload: AnalyticsPayload;
}) {
  opts.payload.$channel = 'PUSH';
  return {
    aps: {
      alert: opts.alert,
      sound: 'default',
    },
    payload: opts.payload,
  };
}

export function androidGenericTemplate(opts: {
  title: string;
  body?: string;
  payload: AnalyticsPayload;
}): {
  notification: { title: string; body?: string; icon: string };
  payload: AnalyticsPayload;
} {
  opts.payload.$channel = 'PUSH';
  return {
    notification: {
      title: opts.title,
      body: opts.body,
      icon: 'ic_launcher',
    },
    payload: opts.payload,
  };
}

export { chatbotMessageTemplates };
